import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './TrackOrder.css';

const TrackOrder = () => {
  const [email, setEmail] = useState('demo@testing.com'); // Pre-filled with correct email
  const [trackingData, setTrackingData] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [selectedOrderIndex, setSelectedOrderIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [debugInfo, setDebugInfo] = useState([]); // Debug logs

  // API Configuration - Updated with correct local backend URL
  const API_BASE_URL = 'http://127.0.0.1:8000/api';

  // Debug logging function
  const addDebugLog = (message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      message,
      data: data ? JSON.stringify(data, null, 2) : null
    };
    
    setDebugInfo(prev => [...prev, logEntry]);
    console.log(`[TRACK ORDER DEBUG] ${message}`, data);
  };

  // API Configuration logging - useEffect to prevent infinite re-renders
  useEffect(() => {
    addDebugLog('API Configuration', {
      API_BASE_URL,
      currentEmail: email,
      timestamp: new Date().toISOString()
    });
  }, []); // Empty dependency array ensures this runs only once on mount

  const transformOrderData = (orderData) => {
    return {
      orderId: orderData.order_id || 'N/A',
      status: orderData.status?.toLowerCase() || 'pending',
      customerInfo: {
        name: orderData.customer?.name || 'N/A',
        email: orderData.customer?.email || email,
        phone: orderData.customer?.phone || 'N/A'
      },
      orderDate: orderData.created_at || new Date().toISOString(),
      estimatedDelivery: orderData.estimated_delivery || null,
      currentLocation: orderData.current_location || 'Processing',
      
      // Transform status history to tracking history
      trackingHistory: orderData.status_history?.slice().reverse().map((historyItem, index, array) => ({
        status: historyItem.status?.toLowerCase() || 'unknown',
        title: historyItem.status || 'Status Update',
        description: historyItem.description || '',
        timestamp: historyItem.changed_at,
        completed: index < array.length - 1, // All except last are completed
        current: index === array.length - 1 // Last one is current
      })) || [{
        status: orderData.status?.toLowerCase() || 'pending',
        title: orderData.status || 'Order Placed',
        description: 'Order has been received and is being processed',
        timestamp: orderData.created_at,
        completed: false,
        current: true
      }],
      
      // Transform order items
      items: orderData.items?.map(item => ({
        name: item.product_name || 'Unknown Product',
        quantity: item.quantity || 1,
        price: parseFloat(item.unit_price || item.total_price || 0),
        image: item.product_image || '/images/products/placeholder.png'
      })) || [],
      
      // Transform shipping address
      shippingAddress: {
        street: orderData.shipping_address?.street_address || '',
        city: orderData.shipping_address?.city || '',
        region: orderData.shipping_address?.region || '',
        country: orderData.shipping_address?.country || ''
      },
      
      totalAmount: parseFloat(orderData.total_amount || 0),
      
      // Additional order details
      orderDetails: {
        subtotal: parseFloat(orderData.subtotal || 0),
        taxAmount: parseFloat(orderData.tax_amount || 0),
        shippingCost: parseFloat(orderData.shipping_cost || 0)
      }
    };
  };

  const transformApiDataToComponentFormat = (apiResponse) => {
    addDebugLog('Transforming API Data', { apiResponse });
    
    if (!apiResponse || !apiResponse.success) {
      addDebugLog('Invalid API response structure', { apiResponse });
      return null;
    }
    
    const orders = apiResponse.orders;
    if (!orders || orders.length === 0) {
      addDebugLog('No orders in API response', { orders });
      return null;
    }

    addDebugLog(`Processing ${orders.length} orders`, { ordersCount: orders.length });

    // Transform all orders
    const transformedOrders = orders.map((orderData, index) => {
      addDebugLog(`Processing order ${index + 1}`, { orderData });
      return transformOrderData(orderData);
    });

    addDebugLog('All orders transformed', { transformedOrders });
    return transformedOrders;
  };

  const handleOrderSelect = (index) => {
    setSelectedOrderIndex(index);
    setTrackingData(allOrders[index]);
    addDebugLog('Order selected', { selectedIndex: index, orderId: allOrders[index]?.orderId });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    addDebugLog('Form submitted', { email });
    
    if (!email.trim()) {
      const errorMsg = 'Please enter your email address';
      setError(errorMsg);
      addDebugLog('Validation error', { error: errorMsg });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      const errorMsg = 'Please enter a valid email address';
      setError(errorMsg);
      addDebugLog('Email validation error', { error: errorMsg });
      return;
    }

    setIsLoading(true);
    setError('');
    setHasSearched(true);
    setTrackingData(null);
    setAllOrders([]);
    setSelectedOrderIndex(0);

    const requestData = {
      email: email.toLowerCase().trim()
    };

    addDebugLog('Starting API request', {
      url: `${API_BASE_URL}/track-order/`,
      method: 'POST',
      requestData
    });

    try {
      const response = await fetch(`${API_BASE_URL}/track-order/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      addDebugLog('API Response received', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries())
      });

      // Read response as text first for debugging
      const responseText = await response.text();
      addDebugLog('Raw API Response', { responseText });

      let data;
      try {
        data = JSON.parse(responseText);
        addDebugLog('Parsed API Response', { data });
      } catch (parseError) {
        addDebugLog('JSON Parse Error', { parseError, responseText });
        throw new Error('Invalid JSON response from server');
      }

      if (!response.ok) {
        addDebugLog('HTTP Error', { status: response.status, data });
        if (response.status === 404) {
          setError('No orders found for this email address. Please check your email and try again.');
          return;
        }
        throw new Error(data.detail || data.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (!data.success) {
        addDebugLog('API returned success=false', { data });
        setError(data.message || 'No orders found for this email address. Please check your email and try again.');
        setTrackingData(null);
        return;
      }

      if (!data.orders || data.orders.length === 0) {
        addDebugLog('No orders in API response', { data });
        setError('No orders found for this email address. Please check your email and try again.');
        setTrackingData(null);
        return;
      }

      addDebugLog('About to transform API data', { 
        apiResponse: data,
        ordersCount: data.orders?.length || 0
      });

      // Transform backend API data to component format (all orders)
      const transformedOrders = transformApiDataToComponentFormat(data);
      
      if (transformedOrders && transformedOrders.length > 0) {
        addDebugLog('Setting all orders data', { transformedOrders });
        setAllOrders(transformedOrders);
        setTrackingData(transformedOrders[0]); // Show first order by default
        setSelectedOrderIndex(0);
      } else {
        const errorMsg = 'Order data could not be processed. Please try again.';
        setError(errorMsg);
        addDebugLog('Data transformation failed', { error: errorMsg });
        setTrackingData(null);
      }
    } catch (err) {
      addDebugLog('API Error', { 
        error: err.message,
        stack: err.stack,
        name: err.name
      });
      
      console.error('API Error Details:', {
        message: err.message,
        stack: err.stack,
        name: err.name,
        cause: err.cause
      });
      
      setError(`An error occurred while tracking your order: ${err.message}. Please check your connection and try again.`);
      setTrackingData(null);
    } finally {
      setIsLoading(false);
      addDebugLog('API request completed');
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      order_confirmed: '✅',
      processing: '⚙️',
      shipped: '🚚',
      in_transit: '🚛',
      out_for_delivery: '🚐',
      delivered: '🎉',
      cancelled: '❌'
    };
    return icons[status] || '📦';
  };

  const getStatusColor = (completed, isCurrent) => {
    if (isCurrent) return 'var(--primary-color)';
    if (completed) return '#28a745';
    return '#e9ecef';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusDisplayName = (status) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to determine if a stage is completed
  const getStageCompletionStatus = (stageKey, currentStatus) => {
    const statusOrder = ['confirmed', 'processing', 'shipped', 'in_transit', 'delivered'];
    const currentIndex = statusOrder.indexOf(currentStatus);
    const stageIndex = statusOrder.indexOf(stageKey);
    
    return stageIndex !== -1 && currentIndex !== -1 && stageIndex < currentIndex;
  };

  // Helper function to determine if a stage is current
  const getStageCurrentStatus = (stageKey, currentStatus) => {
    return stageKey === currentStatus;
  };

  const clearDebugInfo = () => {
    setDebugInfo([]);
    addDebugLog('Debug logs cleared');
  };

  return (
    <div className="track-order-page">
      {/* Hero Section */}
      <section className="track-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Track Your Order</h1>
            <p className="hero-subtitle">
              Enter your email address to track your solar equipment order in real-time
            </p>
            
            <div className="track-hero-stats">
              <div className="stat-item-1">
                <span className="stat-number">24/7</span>
                <span className="stat-label">Order Tracking</span>
              </div>
              <div className="stat-item-1">
                <span className="stat-number">Real-time</span>
                <span className="stat-label">Updates</span>
              </div>
              <div className="stat-item-1">
                <span className="stat-number">100%</span>
                <span className="stat-label">Accuracy</span>
              </div>
              <div className="stat-item-1">
                <span className="stat-number">Secure</span>
                <span className="stat-label">Tracking</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container">
        {/* Tracking Form */}
        <div className="tracking-form-section">
          <div className="tracking-form-card">
            <div className="form-header">
              <h2>Order Tracking</h2>
              <p>Enter the email address used for your order</p>
            </div>

            <form onSubmit={handleSubmit} className="tracking-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    addDebugLog('Email changed', { email: e.target.value });
                  }}
                  placeholder="Enter your email address"
                  required
                  disabled={isLoading}
                />
              </div>

              <button type="submit" className="track-btn" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="spinner"></div>
                    Tracking...
                  </>
                ) : (
                  '🔍 Track Order'
                )}
              </button>
            </form>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Order Selector - Show when multiple orders exist */}
        {allOrders.length > 1 && (
          <div className="order-selector-section">
            <div className="order-selector-card">
              <h3>Select Order to View ({allOrders.length} orders found)</h3>
              <div className="order-selector-grid">
                {allOrders.map((order, index) => (
                  <button
                    key={index}
                    onClick={() => handleOrderSelect(index)}
                    className={`order-selector-item ${selectedOrderIndex === index ? 'active' : ''}`}
                  >
                    <div className="order-selector-header">
                      <span className="order-id">Order #{order.orderId}</span>
                      <span className={`status-badge status-${order.status}`}>
                        {getStatusIcon(order.status)} {getStatusDisplayName(order.status)}
                      </span>
                    </div>
                    <div className="order-selector-details">
                      <span className="order-date">{formatDate(order.orderDate)}</span>
                      <span className="order-amount">P{order.totalAmount.toLocaleString()}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Debug Panel - Only show in development */}
        {debugInfo.length > 0 && (
          <div className="debug-panel" style={{
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            margin: '20px 0'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3>🔧 Debug Information</h3>
              <button 
                onClick={clearDebugInfo}
                style={{
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Clear Logs
              </button>
            </div>
            
            <div style={{ marginTop: '15px' }}>
              <h4>API Configuration:</h4>
              <p><strong>API Base URL:</strong> <code>{API_BASE_URL}</code></p>
              <p><strong>Test Email:</strong> <code>demo@testing.com</code></p>
              <p><strong>Current Email:</strong> <code>{email}</code></p>
              <p><strong>Total Orders:</strong> <code>{allOrders.length}</code></p>
              <p><strong>Selected Order:</strong> <code>{selectedOrderIndex + 1} of {allOrders.length}</code></p>
            </div>

            <div style={{ marginTop: '20px' }}>
              <h4>Debug Logs ({debugInfo.length}):</h4>
              <div style={{ 
                maxHeight: '300px', 
                overflowY: 'auto', 
                background: '#fff', 
                border: '1px solid #dee2e6',
                padding: '10px',
                fontFamily: 'monospace',
                fontSize: '12px'
              }}>
                {debugInfo.map((log, index) => (
                  <div key={index} style={{ marginBottom: '10px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>
                    <div style={{ color: '#2E5C3A', fontWeight: 'bold' }}>
                      [{new Date(log.timestamp).toLocaleTimeString()}] {log.message}
                    </div>
                    {log.data && (
                      <pre style={{ margin: '5px 0 0 0', fontSize: '11px', color: '#666' }}>
                        {log.data}
                      </pre>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Order Tracking Results */}
        {trackingData && (
          <div className="tracking-results">
            {/* Order Summary */}
            <div className="order-summary">
              <div className="order-header">
                <h2>Order #{trackingData.orderId}</h2>
                <div className="order-status">
                  <span className={`status-badge status-${trackingData.status}`}>
                    {getStatusIcon(trackingData.status)} {getStatusDisplayName(trackingData.status)}
                  </span>
                </div>
              </div>

              <div className="order-info-grid">
                <div className="info-item">
                  <label>Customer</label>
                  <span>{trackingData.customerInfo.name}</span>
                </div>
                <div className="info-item">
                  <label>Order Date</label>
                  <span>{formatDate(trackingData.orderDate)}</span>
                </div>
                <div className="info-item">
                  <label>Estimated Delivery</label>
                  <span>{formatDate(trackingData.estimatedDelivery)}</span>
                </div>
                <div className="info-item">
                  <label>Current Location</label>
                  <span>{trackingData.currentLocation}</span>
                </div>
              </div>
            </div>

            {/* Order Progress Bar - Simplified 5 Stages */}
            <div className="order-progress">
              <h3>Order Progress</h3>
              <div className="progress-horizontal">
                {[
                  { key: 'confirmed', label: 'CONFIRMED', icon: '✅' },
                  { key: 'processing', label: 'PROCESSING', icon: '⚙️' },
                  { key: 'shipped', label: 'SHIPPED', icon: '🚚' },
                  { key: 'in_transit', label: 'IN TRANSIT', icon: '🚛' },
                  { key: 'delivered', label: 'DELIVERED', icon: '🎉' }
                ].map((stage, index) => {
                  const isCompleted = getStageCompletionStatus(stage.key, trackingData.status);
                  const isCurrent = getStageCurrentStatus(stage.key, trackingData.status);
                  
                  return (
                    <div key={stage.key} className={`progress-stage ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}>
                      <div className="stage-marker">
                        <span className="stage-icon">{stage.icon}</span>
                      </div>
                      <div className="stage-label">
                        <span className="stage-name">{stage.label}</span>
                      </div>
                      {index < 4 && (
                        <div className="stage-connector"></div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Order Items */}
            <div className="order-items">
              <h3>Order Items</h3>
              <div className="items-grid">
                {trackingData.items.map((item, index) => (
                  <div key={index} className="item-card">
                    <div className="item-image">
                      <img src={item.image} alt={item.name} onError={(e) => { e.target.src = '/images/products/placeholder.png'; }} />
                    </div>
                    <div className="item-details">
                      <h4>{item.name}</h4>
                      <p className="item-quantity">Quantity: {item.quantity}</p>
                      <p className="item-price">P{item.price.toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="shipping-info">
              <h3>Shipping Address</h3>
              <div className="address-card">
                <p>{trackingData.shippingAddress.street}</p>
                <p>{trackingData.shippingAddress.city}, {trackingData.shippingAddress.region}</p>
                <p>{trackingData.shippingAddress.country}</p>
              </div>
            </div>

            {/* Order Total */}
            <div className="order-total">
              <div className="total-card">
                <h3>Order Total</h3>
                <div className="total-amount">
                  <span className="total-label">Total Amount:</span>
                  <span className="total-value">P{trackingData.totalAmount.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Contact Support */}
            <div className="support-section">
              <div className="support-card">
                <h3>Need Help?</h3>
                <p>If you have any questions about your order, our support team is here to help.</p>
                <div className="support-actions">
                  <Link to="/contact" className="support-btn">
                    📧 Contact Support
                  </Link>
                  <a href="https://wa.me/26771548257" target="_blank" rel="noopener noreferrer" className="support-btn whatsapp">
                    📱 WhatsApp Support
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No Results State */}
        {hasSearched && !trackingData && !isLoading && (
          <div className="no-results">
            <div className="no-results-card">
              <div className="no-results-icon">📦</div>
              <h3>No Orders Found</h3>
              <p>We couldn't find any orders associated with this email address.</p>
              <div className="no-results-actions">
                <button onClick={() => { 
                  setEmail('demo@testing.com'); 
                  setTrackingData(null); 
                  setAllOrders([]);
                  setSelectedOrderIndex(0);
                  setError(''); 
                  setHasSearched(false); 
                  setDebugInfo([]);
                  addDebugLog('Form reset to test email');
                }} className="try-again-btn">
                  Try Test Email
                </button>
                <Link to="/contact" className="contact-support-btn">
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackOrder;
